/** Small DOM helpers. No framework: the list is the product, and a framework
 * between the index and the screen is a frame budget spent on nothing. */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") node.className = v;
    else node.setAttribute(k, v);
  }
  for (const c of children) node.append(c);
  return node;
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

/**
 * When a message arrived, in three shapes.
 *
 * `Today @ 9:14 AM` · `Wed, Jan 01 @ 9:14 AM` · `Jan 6th, 2026`
 *
 * Built explicitly rather than with `toLocaleString`, because a locale that
 * renders "September 1, 2026 at 9:14 AM" would push the layout around, and the
 * design forbids that. The year drops out of everything from this year: if it
 * is old enough to need one, the clock time stopped mattering.
 */
export function when(ms: number, now = Date.now()): string {
  const date = new Date(ms);
  const clock = twelveHour(date);
  if (startOfDay(now) === startOfDay(ms)) return `Today @ ${clock}`;
  if (date.getFullYear() === new Date(now).getFullYear()) {
    return `${WEEKDAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${pad(date.getDate())} @ ${clock}`;
  }
  return `${MONTHS[date.getMonth()]} ${ordinal(date.getDate())}, ${date.getFullYear()}`;
}

/** `9:14 AM` — the clock face alone, for a line saying when something last happened. */
export function clock(ms: number): string {
  return twelveHour(new Date(ms));
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** `9:14 AM`, `12:05 PM`. The hour is unpadded, as a clock face reads it. */
function twelveHour(date: Date): string {
  const h = date.getHours();
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${pad(date.getMinutes())} ${h < 12 ? "AM" : "PM"}`;
}

/** 1st, 2nd, 3rd, 4th — and 11th, 12th, 13th, which are the ones that trip it. */
function ordinal(day: number): string {
  const teen = day % 100 >= 11 && day % 100 <= 13;
  const suffix = teen ? "th" : (["th", "st", "nd", "rd"][day % 10] ?? "th");
  return `${day}${suffix}`;
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Up to two initials for a sender.
 *
 * Falls back through the display name, then the address's local part, so a bare
 * `orders@example.com` still gets a letter rather than an empty disc.
 */
export function initials(from: string): string {
  const name = from.replace(/<[^>]*>/, "").replace(/["']/g, "").trim();
  const source = name || from.replace(/<([^>]+)>/, "$1").split("@")[0] || "";
  const words = source.split(/[\s._-]+/).filter(Boolean);
  const letters = words.slice(0, 2).map((w) => w[0] ?? "");
  return (letters.join("") || source.slice(0, 1) || "?").toUpperCase();
}

/**
 * A stable hue per sender, 0–359.
 *
 * Deterministic so a sender keeps their colour across reloads and devices; the
 * disc is tinted, never saturated, so a column of them cannot outshout the names.
 */
export function hueOf(address: string): number {
  let h = 0;
  for (const c of address.trim().toLowerCase()) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
}
